import { useMemo } from 'react';

import { appMenu } from '@/config/menu';
import { ROUTE_ACCESS } from '@/config/route-permissions';
import { MenuItem } from '@/types/menu';

import { useAuthActions } from './useAppContext';

/**
 * Hook to filter menu items based on user permissions
 * Only shows menu items that the user has read permission for
 * SuperAdmin users see all menu items without filtering
 */
export const useFilteredMenu = () => {
  const { hasPermission, isSuperAdmin } = useAuthActions();

  const filteredMenu = useMemo(() => {
    // SuperAdmin users see all menu items without filtering
    if (isSuperAdmin) {
      return appMenu;
    }

    const filterMenuItem = (item: MenuItem): MenuItem | null => {
      // Allow public access items (like dashboard)
      if (item.publicAccess) {
        return item;
      }

      // Check if user has required permission
      if (item.requiresPermission) {
        const { resource, action } = item.requiresPermission;
        if (!hasPermission(resource, action)) {
          return null; // Hide this menu item
        }
      }

      // Handle submenu items
      if (item.hasSubMenu && item.subMenu) {
        const filteredSubMenu = item.subMenu
          .map(filterMenuItem)
          .filter((subItem): subItem is MenuItem => subItem !== null);

        // If no submenu items are visible, hide the parent menu
        if (filteredSubMenu.length === 0) {
          return null;
        }

        return {
          ...item,
          subMenu: filteredSubMenu,
        };
      }

      return item;
    };

    return appMenu
      .map(filterMenuItem)
      .filter((item): item is MenuItem => item !== null);
  }, [hasPermission, isSuperAdmin]);

  return filteredMenu;
};

/**
 * Hook to check if a user can access a specific route
 * SuperAdmin users can access all routes
 */
export const useRoutePermission = (path: string) => {
  const { hasPermission, isSuperAdmin } = useAuthActions();

  return useMemo(() => {
    // SuperAdmin users can access all routes
    if (isSuperAdmin) {
      return true;
    }

    // Dashboard is always accessible
    if (path === '/' || path === '/dashboard') {
      return true;
    }

    // Find the menu item that matches this path
    const findMenuItemByPath = (items: MenuItem[]): MenuItem | null => {
      for (const item of items) {
        // Exact match
        if (item.path === path) {
          return item;
        }
        // Support detail routes by matching subpaths like "/customers/:id"
        // Avoid matching root '/' or placeholder '#'
        if (
          item.path &&
          item.path !== '/' &&
          item.path !== '#' &&
          path.startsWith(item.path + '/')
        ) {
          return item;
        }
        if (item.subMenu) {
          const found = findMenuItemByPath(item.subMenu);
          if (found) return found;
        }
      }
      return null;
    };

    const menuItem = findMenuItemByPath(appMenu);

    if (!menuItem) {
      // If not in menu, fallback to explicit route access map
      const rule = ROUTE_ACCESS[path];
      if (!rule) return false;
      if (rule.publicAccess) return true;
      if (rule.requiresPermission) {
        const { resource, action } = rule.requiresPermission;
        return hasPermission(resource, action);
      }
      return false;
    }

    // Check public access
    if (menuItem.publicAccess) {
      return true;
    }

    // Check permission requirements
    if (menuItem.requiresPermission) {
      const { resource, action } = menuItem.requiresPermission;
      return hasPermission(resource, action);
    }

    // If no permission specified, allow access (legacy support)
    return true;
  }, [path, hasPermission, isSuperAdmin]);
};
